import { themeAtom } from "../../../shared/atoms";
import { AdminCronRunResult } from "../../models/admin-cron.models";
import { adminStyles } from "../../styles/admin.styles";
import {
  formatCronValue,
  getCronResultMetrics,
  getCronResultSummary,
  getObjectEntries,
} from "../../utils/admin-cron.utils";
import { useAtomValue } from "jotai";
import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";

interface Props {
  result: AdminCronRunResult;
}

const metricToneClasses = {
  success: "bg-[#CCE4E6] text-[#007681]",
  warning: "bg-[#FFF8E1] text-[#946200]",
  danger: "bg-[#FFEAED] text-[#BF0002]",
  neutral: "bg-gray-50 text-gray-700",
};

const metricPreviewClassName = "bg-gray-100 text-gray-400 opacity-70";
const previewMetrics = ["Status", "Mode", "Run flag", "Items", "Errors", "Duration"];
const rawResponseOnlyEndpoints = new Set([
  "run-certificates-kpis-daily-snapshot-dev",
  "run-certificates-kpis-daily-snapshot-prod",
]);

const renderValueBlock = (label: string, value: unknown) => (
  <div key={label} className="min-w-0">
    <p className="text-[11px] font-semibold uppercase opacity-70">{label}</p>
    <pre className="mt-1 max-h-36 overflow-auto whitespace-pre-wrap break-words text-xs leading-5">
      {formatCronValue(value)}
    </pre>
  </div>
);

const CronResultMetrics = ({ result }: Props) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-test="admin-cron-result-metrics">
    {getCronResultMetrics(result).map((metric) => (
      <MetricTile
        key={metric.label}
        label={metric.label}
        value={metric.value}
        className={metricToneClasses[metric.tone]}
      />
    ))}
  </div>
);

const MetricTile = ({ label, value, className }: { label: string; value: string | number; className: string }) => (
  <div
    className={twMerge(adminStyles.card.indicatorCompact, className)}
    data-test={`admin-cron-result-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <p className="text-[10px] font-semibold uppercase opacity-75">{label}</p>
    <p className="mt-2 text-4xl font-bold leading-none">{value}</p>
  </div>
);

const ResultSection = ({ title, children }: { title: string; children: ReactNode }) => {
  const theme = useAtomValue(themeAtom);

  return (
    <section className={twMerge("p-4 shadow-sm", theme.home.tailwind.panelBackground)}>
      <h4 className={twMerge("mb-3 text-sm font-semibold", theme.home.tailwind.textDefault)}>{title}</h4>
      <div className="grid gap-3">{children}</div>
    </section>
  );
};

const CronPayloadHighlights = ({ result }: Props) => {
  const healthEntries = getObjectEntries((result.data as { health?: unknown } | undefined)?.health);
  const detailEntries = getObjectEntries((result.data as { details?: unknown } | undefined)?.details);
  const errorEntries = getObjectEntries((result.data as { errors?: unknown } | undefined)?.errors);
  const messagePreviewLines = (result.data as { messagePreviewLines?: unknown } | undefined)?.messagePreviewLines;
  const previewLines = (result.data as { previewLines?: unknown } | undefined)?.previewLines;
  const previewTitle =
    result.endpoint === "admin/debug/tech-form-inbox/preview" ? "Detected JSON attachments" : "Preview";

  return (
    <div className="grid gap-4 xl:grid-cols-2" data-test="admin-cron-result-highlights">
      {healthEntries.length > 0 && (
        <ResultSection title="Health">
          {healthEntries.map(([key, value]) => renderValueBlock(key, value))}
        </ResultSection>
      )}

      {Array.isArray(messagePreviewLines) && messagePreviewLines.length > 0 && (
        <ResultSection title="Mailbox emails and OTF status">
          <ol className="space-y-1 text-sm" data-test="admin-cron-mailbox-email-list">
            {messagePreviewLines.map((line) => (
              <li key={String(line)}>{String(line)}</li>
            ))}
          </ol>
        </ResultSection>
      )}

      {Array.isArray(previewLines) && previewLines.length > 0 && (
        <ResultSection title={previewTitle}>
          <ul className="space-y-1 text-sm">
            {previewLines.map((line) => (
              <li key={String(line)}>{String(line)}</li>
            ))}
          </ul>
        </ResultSection>
      )}

      {detailEntries.length > 0 && (
        <ResultSection title="Details">
          {detailEntries.slice(0, 6).map(([key, value]) => renderValueBlock(key, value))}
        </ResultSection>
      )}

      {errorEntries.length > 0 && (
        <ResultSection title="Errors">
          {errorEntries.slice(0, 6).map(([key, value]) => renderValueBlock(key, value))}
        </ResultSection>
      )}
    </div>
  );
};

/**
 * Disabled result metrics preview the response shape before a cron endpoint is run.
 */
export const CronJobRunPreview = () => {
  const theme = useAtomValue(themeAtom);

  return (
    <section data-test="admin-cron-result-preview" className={twMerge(adminStyles.card.surface, "p-4")}>
      <div className="mb-3 flex items-end justify-between gap-4">
        <p className={twMerge("text-sm font-semibold", theme.home.tailwind.textDefault)}>Response indicators</p>
        <p className={twMerge("text-xs", theme.uwhcAssistant.text)}>Preview until an endpoint returns.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {previewMetrics.map((metric) => (
          <MetricTile key={metric} label={metric} value="--" className={metricPreviewClassName} />
        ))}
      </div>
    </section>
  );
};

/**
 * Formats heterogeneous cron responses into consistent operational indicators.
 */
const CronJobRunResult = ({ result }: Props) => {
  const theme = useAtomValue(themeAtom);

  return (
    <section data-test="admin-cron-result" className={twMerge(adminStyles.card.surface, "p-4")}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className={twMerge("text-sm font-semibold", theme.home.tailwind.textDefault)}>
            {result.variantLabel} finished at {new Date(result.finishedAt).toLocaleTimeString()}
          </p>
          <p className={twMerge("mt-1 text-sm", theme.uwhcAssistant.textOutput)}>{getCronResultSummary(result)}</p>
        </div>
        <code className={twMerge("px-2 py-1 text-xs", theme.home.tailwind.pageBackground)}>{result.endpoint}</code>
      </div>

      <CronResultMetrics result={result} />
    </section>
  );
};

export const CronJobRunDetails = ({ result }: Props) => {
  const theme = useAtomValue(themeAtom);
  const shouldShowPayloadHighlights = !rawResponseOnlyEndpoints.has(result.endpoint);

  return (
    <section data-test="admin-cron-result-details" className="pt-5">
      <div className="space-y-4">
        {shouldShowPayloadHighlights && <CronPayloadHighlights result={result} />}
        <details className={twMerge("p-4 shadow-sm", theme.home.tailwind.panelBackground)}>
          <summary className="cursor-pointer text-sm font-semibold">Raw response</summary>
          <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs leading-5">
            {formatCronValue(result.status === "error" ? result.errorMessage : result.data)}
          </pre>
        </details>
      </div>
    </section>
  );
};

export default CronJobRunResult;
